import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import usePageMeta from "@/lib/usePageMeta";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowRight, MessageCircleQuestion } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

export default function Faq() {
  const { t } = useTranslation();
  usePageMeta("faq");
  const items = t("faq.items", { returnObjects: true });
  const [open, setOpen] = useState(0);

  return (
    <div className="min-h-screen bg-bg text-[var(--text)]">
      <PublicHeader />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-8 text-center">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-c bg-secondary-c/10 px-3 py-1 rounded-full">
          <MessageCircleQuestion className="w-3.5 h-3.5" /> {t("faq_page.badge")}
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-tight">
          {t("faq.title")} <span className="font-serif-italic text-primary-c">{t("faq.title_accent")}</span>
        </motion.h1>
        <p className="mt-4 text-text-secondary text-base sm:text-lg max-w-xl mx-auto">{t("faq_page.subtitle")}</p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-14">
        <div className="space-y-3">
          {(Array.isArray(items) ? items : []).map((item, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              type="button"
              onClick={() => setOpen(open === i ? -1 : i)}
              data-testid={`faq-item-${i}`}
              className="w-full text-left card-c hover:shadow-md transition-shadow block"
            >
              <div className="flex items-start justify-between gap-4">
                <p className="font-semibold text-base">{item.q}</p>
                <ChevronDown className={`w-5 h-5 shrink-0 text-text-secondary transition-transform ${open === i ? "rotate-180" : ""}`} />
              </div>
              <AnimatePresence>
                {open === i && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    className="mt-3 text-sm text-text-secondary leading-relaxed overflow-hidden"
                  >
                    {item.a}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.button>
          ))}
        </div>

        <div className="mt-10 text-center card-c">
          <h3 className="font-display text-xl">{t("faq_page.more_title")}</h3>
          <p className="mt-2 text-sm text-text-secondary">{t("faq_page.more_desc")}</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
              {t("faq_page.write_us")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/reserver" className="btn-secondary inline-flex items-center justify-center gap-2">
              {t("book.cta")}
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
